import React, { useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import SignatureCanvas from "react-signature-canvas";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Upload,
  message,
  Typography,
  Card,
  Row,
  Col,
  Spin,
  Layout,
  Space,
  Checkbox,
} from "antd";
import { UploadOutlined, CarOutlined } from "@ant-design/icons";
import moment from "moment";
import { refs, storage } from "./firebase/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { addDoc } from "firebase/firestore";
import { generatePDF, sendEmail } from "./utils";
import { questions } from "./config";
import { Link } from "react-router-dom";

const { Title } = Typography;
const { TextArea } = Input;
const { Content } = Layout;

function App() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState(null);
  const sigCanvas = useRef(null);

  const date = new Date();

  const uploadFile = async (file, path) => {
    if (!file) return null;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleStep1Submit = async (values) => {
    setLoading(true);
    try {
      const uploadTasks = [
        uploadFile(
          values.registrationPaper[0].originFileObj,
          `registration_papers/${values.ownerName}_${Date.now()}.pdf`
        ),
        uploadFile(
          values.vehicleInsurance[0].originFileObj,
          `vehicle_insurance/${values.ownerName}_${Date.now()}.pdf`
        ),
        uploadFile(
          values.cameraCertificate[0].originFileObj,
          `camera_certificates/${values.ownerName}_${Date.now()}.pdf`
        ),
        uploadFile(
          values.licensePhoto[0].originFileObj,
          `license_photos/${values.ownerName}_${Date.now()}.pdf`
        ),
        uploadFile(
          values.pinkSlip[0].originFileObj,
          `pink_slip/${values.ownerName}_${Date.now()}.pdf`
        ),
        uploadFile(
          values.driversLicense[0].originFileObj,
          `drivers_license/${values.ownerName}_${Date.now()}.pdf`
        ),
        values.companyName && values.certificateOfIncorporation
          ? uploadFile(
              values.certificateOfIncorporation[0].originFileObj,
              `certificates/${values.ownerName}_${Date.now()}.pdf`
            )
          : Promise.resolve(null),
      ];

      const [
        registrationPaperURL,
        vehicleInsuranceURL,
        cameraCertificateURL,
        licensePhotoURL,
        pinkSlipURL,
        driversLicenseURL,
        certificateURL,
      ] = await Promise.all(uploadTasks);

      const step1DataToStore = {
        ...values,
        registrationPaper: registrationPaperURL,
        vehicleInsurance: vehicleInsuranceURL,
        cameraCertificate: cameraCertificateURL,
        licensePhoto: licensePhotoURL,
        pinkSlip: pinkSlipURL,
        driversLicense: driversLicenseURL,
        certificateOfIncorporation: certificateURL ? certificateURL : null,
        dob: values.dob.toDate(),
        registrationPaperExpiry: values.registrationPaperExpiry.toDate(),
        vehicleInsuranceExpiry: values.vehicleInsuranceExpiry.toDate(),
        cameraCertificateExpiry: values.cameraCertificateExpiry.toDate(),
        licensePhotoExpiry: values.licensePhotoExpiry.toDate(),
        pinkSlipExpiry: values.pinkSlipExpiry.toDate(),
        driversLicenseExpiry: values.driversLicenseExpiry.toDate(),
        createdAt: new Date(),
      };

      if (!values.companyName) {
        delete step1DataToStore.companyName;
        delete step1DataToStore.certificateOfIncorporation;
      }

      setStep1Data(step1DataToStore);
      setCurrentStep(2);
    } catch (error) {
      console.error("Error processing step 1:", error);
      message.error("An error occurred while processing your information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (values) => {
    setLoading(true);
    try {
      const completeData = {
        ...step1Data,
        acceptTerms: values.acceptTerms,
      };

      const mappedValues = questions.reduce((acc, { key, question }) => {
        if (completeData[key] !== undefined) {
          acc[question] = completeData[key];
        }
        return acc;
      }, {});

      const image = sigCanvas.current.toDataURL("image/jpeg", {});
      const formResponse = await generatePDF(mappedValues, image,step1Data);
      console.log('formResponse: ', formResponse);

      await sendEmail("Info@operataxis.com", formResponse);
      await sendEmail(step1Data.email, formResponse);

      await addDoc(refs.vehicleInfo, {
        ...completeData,
        formResponse: formResponse,
      });

      message.success("Form submitted successfully!");
      // Reset form if needed
      form.resetFields();
      sigCanvas.current.clear();
      setCurrentStep(1)
    } catch (error) {
      console.error("Error submitting form:", error);
      message.error("An error occurred while submitting the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    console.log('values: ', values);
    if (currentStep === 1) {
      handleStep1Submit(values);
    } else {
      handleStep2Submit(values);
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "16px" }}>
        <Row justify="center">
          <Col xs={24} sm={24} md={20} lg={18} xl={16}>
            <Card
              hoverable
              style={{
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                background: "linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%)",
              }}
            >
              <Spin spinning={loading}>
                <Title
                  level={2}
                  style={{
                    textAlign: "center",
                    marginBottom: "24px",
                    color: "#001f3f",
                    fontSize: "24px",
                  }}
                >
                  <Space>
                    <CarOutlined />
                    Vehicle Information Form
                  </Space>
                </Title>

                <Form
                  form={form}
                  name="vehicleInfoForm"
                  onFinish={onFinish}
                  layout="vertical"
                >
                  {currentStep === 1 ? (
                    <>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="ownerName"
                            label="Owner Name"
                            rules={[
                              {
                                required: true,
                                message: "Please input the owner name!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="dob"
                            label="Date of Birth"
                            rules={[
                              {
                                required: true,
                                message: "Please select your date of birth!",
                              },
                            ]}
                          >
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                              {
                                required: true,
                                type: "email",
                                message: "Please input a valid email!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="phone"
                            label="Phone"
                            rules={[
                              {
                                required: true,
                                message: "Please input the phone number!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="abnNumber"
                            label="ABN Number"
                            rules={[
                              {
                                required: true,
                                message: "Please input the ABN number!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="companyName"
                            label="Company Name (If vehicle is under company name)"
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues.companyName !== currentValues.companyName
                        }
                      >
                        {({ getFieldValue }) =>
                          getFieldValue("companyName") ? (
                            <Row gutter={[16, 0]}>
                              <Col xs={24}>
                                <Form.Item
                                  name="certificateOfIncorporation"
                                  label="Certificate of Incorporation"
                                  valuePropName="fileList"
                                  getValueFromEvent={normFile}
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        "Please upload the Certificate of Incorporation!",
                                    },
                                  ]}
                                >
                                  <Upload
                                    name="certificateOfIncorporation"
                                    accept=".pdf"
                                    beforeUpload={() => false}
                                    maxCount={1}
                                  >
                                    <Button
                                      icon={<UploadOutlined />}
                                      style={{ width: "100%" }}
                                    >
                                      Upload Certificate of Incorporation
                                    </Button>
                                  </Upload>
                                </Form.Item>
                              </Col>
                            </Row>
                          ) : null
                        }
                      </Form.Item>
                      <Row gutter={[16, 0]}>
                        <Col xs={24}>
                          <Form.Item
                            name="address"
                            label="Address"
                            rules={[
                              {
                                required: true,
                                message: "Please input the address!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="registrationPaper"
                            label="Registration Paper"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[
                              {
                                required: true,
                                message: "Please upload the registration paper!",
                              },
                            ]}
                          >
                            <Upload
                              name="registrationPaper"
                              accept=".pdf"
                              beforeUpload={() => false}
                              maxCount={1}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                style={{ width: "100%" }}
                              >
                                Upload Registration Paper
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="registrationPaperExpiry"
                            label="Registration Paper Expiry"
                            rules={[
                              {
                                required: true,
                                message: "Please select the expiry date!",
                              },
                            ]}
                          >
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="vehicleInsurance"
                            label="Vehicle Insurance"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[
                              {
                                required: true,
                                message: "Please upload the vehicle insurance!",
                              },
                            ]}
                          >
                            <Upload
                              name="vehicleInsurance"
                              accept=".pdf"
                              beforeUpload={() => false}
                              maxCount={1}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                style={{ width: "100%" }}
                              >
                                Upload Vehicle Insurance
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="vehicleInsuranceExpiry"
                            label="Vehicle Insurance Expiry"
                            rules={[
                              {
                                required: true,
                                message: "Please select the expiry date!",
                              },
                            ]}
                          >
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="cameraCertificate"
                            label="Camera Certificate"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[
                              {
                                required: true,
                                message: "Please upload the camera certificate!",
                              },
                            ]}
                          >
                            <Upload
                              name="cameraCertificate"
                              accept=".pdf"
                              beforeUpload={() => false}
                              maxCount={1}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                style={{ width: "100%" }}
                              >
                                Upload Camera Certificate
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="cameraCertificateExpiry"
                            label="Camera Certificate Expiry"
                            rules={[
                              {
                                required: true,
                                message: "Please select the expiry date!",
                              },
                            ]}
                          >
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="licensePhoto"
                            label="License Photo"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[
                              {
                                required: true,
                                message: "Please upload the license photo!",
                              },
                            ]}
                          >
                            <Upload
                              name="licensePhoto"
                              accept=".pdf"
                              beforeUpload={() => false}
                              maxCount={1}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                style={{ width: "100%" }}
                              >
                                Upload License Photo
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="licensePhotoExpiry"
                            label="License Expiry Date"
                            rules={[
                              {
                                required: true,
                                message: "Please select the expiry date!",
                              },
                            ]}
                          >
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="pinkSlip"
                            label="Pink Slip"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[
                              {
                                required: true,
                                message: "Please upload the pink slip!",
                              },
                            ]}
                          >
                            <Upload
                              name="pinkSlip"
                              accept=".pdf,.png,.jpg,.jpeg"
                              beforeUpload={() => false}
                              maxCount={1}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                style={{ width: "100%" }}
                              >
                                Upload Pink Slip
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="pinkSlipExpiry"
                            label="Pink Slip Expiry"
                            rules={[
                              {
                                required: true,
                                message: "Please select the expiry date!",
                              },
                            ]}
                          >
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="driversLicense"
                            label="Driver's License"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[
                              {
                                required: true,
                                message: "Please upload the driver's license!",
                              },
                            ]}
                          >
                            <Upload
                              name="driversLicense"
                              accept=".pdf,.png,.jpg,.jpeg"
                              beforeUpload={() => false}
                              maxCount={1}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                style={{ width: "100%" }}
                              >
                                Upload Driver's License
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="driversLicenseExpiry"
                            label="Driver's License Expiry"
                            rules={[
                              {
                                required: true,
                                message: "Please select the expiry date!",
                              },
                            ]}
                          >
                            <DatePicker style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Title
                        level={4}
                        style={{
                          marginTop: "24px",
                          marginBottom: "16px",
                          color: "#001f3f",
                        }}
                      >
                        Banking Information
                      </Title>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="bankName"
                            label="Name of Bank"
                            rules={[
                              {
                                required: true,
                                message: "Please input the bank name!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="branchName"
                            label="Branch Name"
                            rules={[
                              {
                                required: true,
                                message: "Please input the branch name!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="bsbNumber"
                            label="BSB Number"
                            rules={[
                              {
                                required: true,
                                message: "Please input the BSB number!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="accountNumber"
                            label="Account Number"
                            rules={[
                              {
                                required: true,
                                message: "Please input the account number!",
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        name="notes"
                        label="Notes"
                        rules={[
                          { required: true, message: "Please input notes!" },
                        ]}
                      >
                        <TextArea rows={4} />
                      </Form.Item>
                    </>
                  ) : (
                    <>
                      
                    <title>Opera Taxis Operator Compliance Contract</title>

                    <h1>Opera Taxis Operator Compliance Contract</h1>
                    <p>
                      This Contract is entered into between Opera Taxis (the
                      "Network") and [Registered Owner of the Taxi] (the
                      "Operator").
                    </p>

                    <h2>1. Purpose</h2>
                    <p>
                      The purpose of this Contract is to ensure that the
                      Operator fully complies with all relevant policies and
                      procedures established by Opera Taxis. The Operator
                      acknowledges and agrees to abide by these policies as a
                      condition of affiliation with Opera Taxis.
                    </p>

                    <h2>2. Operator Obligations</h2>
                    <p>The Operator agrees to:</p>
                    <ul>
                      <li>
                        Comply with all Opera Taxis policies and procedures,
                        including but not limited to:
                        <strong><ul>
                          <li>Complaint Handling Procedure</li>
                          <li>Drug and Alcohol Policy</li>
                          <li>Fatigue Management Policy</li>
                          <li>Incident Management Policy</li>
                          <li>Network By-Laws</li>
                          <li>Safety Management System (SMS)</li>
                          <li>Vehicle Maintenance and Roadworthiness Policy</li>
                        </ul>
                        </strong>
                      </li>
                      <strong>
                      <li>
                        Ensure that all drivers affiliated with the Operator
                        adhere to these policies.
                      </li>
                      <li>
                        Monitor compliance regularly and take immediate
                        corrective actions when non-compliance is identified.
                      </li>
                      </strong>
                    </ul>

                    <h2>3. Policy Compliance</h2>
                    <p>
                      The Operator agrees to fully understand and implement the
                      following policies:
                    </p>
                    <strong>
                    <ul>
                      <li>
                        <strong>Complaint Handling Procedure:</strong> Ensure
                        complaints are handled promptly and professionally,
                        maintaining confidentiality and impartiality at all
                        times.
                      </li>
                      <li>
                        <strong>Drug and Alcohol Policy:</strong> Maintain a
                        zero-tolerance approach to drug and alcohol use and
                        ensure all affiliated drivers are aware of the
                        consequences of violations.
                      </li>
                      <li>
                        <strong>Fatigue Management Policy:</strong> Monitor
                        driver fatigue and ensure compliance with regulated
                        driving hours and mandatory rest periods.
                      </li>
                      <li>
                        <strong>Incident Management Policy:</strong> Respond
                        effectively to any incidents involving drivers or
                        vehicles, including immediate reporting and accurate
                        documentation.
                      </li>
                      <li>
                        <strong>Network By-Laws:</strong> Adhere to all
                        mandatory and prohibited conduct as outlined in the
                        by-laws, including maintaining valid insurance, vehicle
                        safety, and roadworthiness.
                      </li>
                      <li>
                        <strong>Safety Management System (SMS):</strong>{" "}
                        Implement measures to manage risks and ensure the safety
                        of passengers, drivers, and the public.
                      </li>
                      <li>
                        <strong>Vehicle Maintenance Policy:</strong> Conduct
                        regular maintenance and safety inspections, keeping
                        records of all repairs and servicing.
                      </li>
                    </ul>
                    </strong>

                    <h2>4. Reporting and Record Keeping</h2>
                    <p>The Operator must:</p>
                    <ul>
                      <li>
                        Maintain accurate and complete records as required by
                        Opera Taxis policies.
                      </li>
                      <li>
                        Submit reports promptly, including but not limited to
                        incident reports, maintenance logs, and compliance
                        checklists.
                      </li>
                      <li>
                        Cooperate with any audits or inspections conducted by
                        Opera Taxis or relevant regulatory authorities.
                      </li>
                    </ul>

                    <h2>5. Non-Compliance and Consequences</h2>
                    <p>
                      Failure to comply with this Contract or any Opera Taxis
                      policies may result in disciplinary action, including
                      suspension or termination of the Operator’s affiliation.
                    </p>

                    <h2>6. Amendments</h2>
                    <p>
                      Opera Taxis reserves the right to update this Contract and
                      related policies. Operators will be notified of any
                      changes and are deemed to accept such changes by
                      continuing their affiliation.
                    </p>

                    <h2>7. Governing Law</h2>
                    <p>
                      This Contract is governed by the laws of New South Wales,
                      Australia.
                    </p>

                    <h2>8. Unpaid Dues and Collections</h2>
                    <p>
                      If any amount remains unpaid, Opera Taxis reserves the
                      right to engage credit collection agencies to recover
                      outstanding funds. The Operator will be responsible for
                      all costs associated with the debt recovery process.
                    </p>

                    <h2>9. Driver Fine Liability</h2>
                    <p>
                      In the event of any overcharged fine amount being due from
                      a driver affiliated with the Operator’s taxi, the Operator
                      will be equally responsible and liable to pay the dues if
                      the driver fails to reimburse in a timely manner.
                    </p>

                    <h2>10. Acknowledgment and Signature</h2>
                    
                    <p>
                      By signing this Contract, the Operator acknowledges that
                      they have read, understood, and agreed to abide by all
                      terms and policies referenced here in.
                    </p>
                    <Form.Item
                      name="acceptTerms"
                      valuePropName="checked"
                      rules={[
                        {
                          validator: (_, value) =>
                            value
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(
                                    "Please accept the terms and conditions"
                                  )
                                ),
                        },
                      ]}
                    >
                      <Checkbox
                        onChange={(e) => {
                          form.setFieldsValue({
                            acceptTerms: e.target.checked ? "agree" : undefined,
                          });
                        }}
                      >
                        I accept all the Terms and Conditions and confirm that
                        all information provided is accurate and complete.
                      </Checkbox>
                    </Form.Item>
                    <p>
                      <strong>Operator Name:</strong> 
                    {" "}  {step1Data?.ownerName}
                    </p>
                    <p>
                      <strong>Date:</strong> {" "}
                      {`${date.getDate()}/${
                        date.getMonth() + 1
                      }/${date.getFullYear()}`}
                      
                    </p>
                    {/* <p><strong>Signature:</strong> _______________________</p> */}
                    <p>
                      <strong> Sign :</strong>
                    </p>

                     
                     

                      <SignatureCanvas
                        penColor="black"
                        backgroundColor="white"
                        canvasProps={{
                          width: 350,
                          height: 200,
                          className: "sigCanvas",
                        }}
                        ref={sigCanvas}
                      />
                    </>
                  )}

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      block
                      size="large"
                      style={{
                        background: "#d68028",
                        borderColor: "#d68028",
                        height: "48px",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      {currentStep === 1 ? "Next" : "Submit"}
                    </Button>
                  </Form.Item>
                </Form>
              </Spin>
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}

export default App;